/**
 * SendingProgress
 *
 * Premium inline progress tracking for correspondence transmission.
 * Shows smooth step-by-step progress with real-time status polling.
 * Inspired by: Stripe payment processing, Airbnb booking flow
 */

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  CorrespondenceChannel,
  TransmissionStatus,
} from '@/services/api/correspondenceService';
import { useCorrespondencePackageRetrieveQuery } from '@/services/api/correspondenceService';

export interface SendingProgressProps {
  workflowId: string;
  packageId: string;
  matterId: string;
  channel: CorrespondenceChannel;
  recipientName: string;
  onComplete: () => void;
  onRetry?: () => void;
}

// ============================================================================
// Step Configuration
// ============================================================================

interface Step {
  key: string;
  label: string;
}

const CHANNEL_STEPS: Record<string, Step[]> = {
  fax: [
    { key: 'preparing', label: 'Preparing fax' },
    { key: 'sending', label: 'Transmitting' },
    { key: 'confirming', label: 'Confirming delivery' },
  ],
  email: [
    { key: 'preparing', label: 'Preparing email' },
    { key: 'sending', label: 'Sending via Front' },
    { key: 'confirming', label: 'Confirming delivery' },
  ],
  mailed_letter: [
    { key: 'preparing', label: 'Creating letter' },
    { key: 'sending', label: 'Submitting to postal service' },
    { key: 'confirming', label: 'Confirming submission' },
  ],
  mailed_cheque: [
    { key: 'preparing', label: 'Creating cheque' },
    { key: 'sending', label: 'Submitting to postal service' },
    { key: 'confirming', label: 'Confirming submission' },
  ],
};

function getStepIndex(status: TransmissionStatus): number {
  switch (status) {
    case 'pending':
      return 0;
    case 'sending':
      return 1;
    case 'sent':
    case 'delivered':
      return 2;
    default:
      return 0;
  }
}

// ============================================================================
// Component
// ============================================================================

export const SendingProgress: React.FC<SendingProgressProps> = ({
  workflowId,
  packageId,
  matterId,
  channel,
  recipientName,
  onComplete,
  onRetry,
}) => {
  const [isComplete, setIsComplete] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const steps = CHANNEL_STEPS[channel] || CHANNEL_STEPS.fax;

  // Poll package to get message status (workflow creates message, updates status)
  const shouldStopPolling = isComplete || hasFailed;
  const { data: packageData, isError } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId },
    {
      pollingInterval: shouldStopPolling ? 0 : 1000,
      skipPollingIfUnfocused: false,
    },
  );

  // Get status from the message attached to the package
  const messageStatus = packageData?.message?.transmission_status || 'pending';
  const errorMessage = packageData?.message?.transmission_error;
  const currentStep = getStepIndex(messageStatus as TransmissionStatus);

  console.log('SendingProgress render:', {
    workflowId,
    messageId: packageData?.message?.id,
    messageStatus,
    isComplete,
    hasFailed,
    shouldStopPolling,
  });

  // Handle completion states
  useEffect(() => {
    if (!messageStatus) {
      console.log('No message status yet');
      return;
    }

    console.log('Progress status:', messageStatus, {
      hasFailed,
      isComplete,
    });

    // Check for success
    if (messageStatus === 'sent' || messageStatus === 'delivered') {
      if (!isComplete) {
        console.log('✅ Setting isComplete to true');
        setIsComplete(true);
        // Note: Auto-complete disabled - user stays on the page to see success state
      }
    }
    // Check for failure
    else if (messageStatus === 'failed' || messageStatus === 'bounced') {
      if (!hasFailed) {
        console.log('❌ Setting hasFailed to true');
        setHasFailed(true);
      }
    }
  }, [messageStatus, isComplete, onComplete, hasFailed]);

  // Handle query errors
  useEffect(() => {
    if (isError && !hasFailed) {
      setHasFailed(true);
    }
  }, [isError, hasFailed]);

  const handleRetry = () => {
    setHasFailed(false);
    setIsComplete(false);
    onRetry?.();
  };

  return (
    <div className="space-y-6">
      {/* Header - contextual messaging */}
      <div className="text-center">
        <AnimatePresence mode="wait">
          {isComplete ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="space-y-2"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="flex justify-center"
              >
                <div className="relative">
                  <CheckCircleIcon className="h-12 w-12 text-emerald-500" />
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.8, opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0 rounded-full bg-emerald-500"
                  />
                </div>
              </motion.div>
              <h3 className="text-lg font-semibold text-zinc-900">
                Sent successfully!
              </h3>
              <p className="text-sm text-zinc-600">
                Delivered to{' '}
                <span className="font-medium text-zinc-900">
                  {recipientName}
                </span>
              </p>
            </motion.div>
          ) : hasFailed ? (
            <motion.div
              key="failed"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="space-y-2"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="flex justify-center"
              >
                <XCircleIcon className="h-12 w-12 text-red-500" />
              </motion.div>
              <h3 className="text-lg font-semibold text-zinc-900">
                Send failed
              </h3>
              <p className="text-sm text-zinc-600">
                Could not deliver to{' '}
                <span className="font-medium text-zinc-900">
                  {recipientName}
                </span>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="sending"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="space-y-2"
            >
              <h3 className="text-lg font-semibold text-zinc-900">
                Sending to {recipientName}
              </h3>
              <p className="text-sm text-zinc-600">
                Processing your transmission...
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Progress Steps - only show when sending */}
      {!isComplete && !hasFailed && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-3"
        >
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isDone = index < currentStep;

            return (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                {/* Step indicator */}
                <div className="flex-shrink-0">
                  <AnimatePresence mode="wait">
                    {isDone ? (
                      <motion.div
                        key="done"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                      </motion.div>
                    ) : isActive ? (
                      <motion.div
                        key="active"
                        className="h-5 w-5 rounded-full border-2 border-indigo-600 flex items-center justify-center relative"
                      >
                        <motion.div
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                          className="h-2.5 w-2.5 rounded-full bg-indigo-600"
                        />
                        <motion.div
                          animate={{ scale: [1, 2], opacity: [0.5, 0] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                          className="absolute inset-0 rounded-full bg-indigo-600"
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="pending"
                        className="h-5 w-5 rounded-full border-2 border-zinc-300 bg-white"
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Step label */}
                <p
                  className={cn(
                    'text-sm font-medium transition-colors',
                    isDone
                      ? 'text-emerald-700'
                      : isActive
                        ? 'text-indigo-700'
                        : 'text-zinc-400',
                  )}
                >
                  {step.label}
                </p>
              </motion.div>
            );
          })}

          {/* Progress bar */}
          <div className="pt-2">
            <div className="bg-zinc-100 rounded-full h-1 overflow-hidden">
              <motion.div
                initial={{ width: '0%' }}
                animate={{
                  width: `${((currentStep + 1) / steps.length) * 100}%`,
                }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600"
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Error message */}
      {hasFailed && errorMessage && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-red-50 border border-red-100 rounded-lg p-3"
        >
          <p className="text-xs font-medium text-red-800 mb-1">Error Details</p>
          <p className="text-xs text-red-700">{errorMessage}</p>
        </motion.div>
      )}

      {/* Retry button on failure */}
      {hasFailed && onRetry && (
        <Button onClick={handleRetry} className="w-full" size="lg">
          Try Again
        </Button>
      )}
    </div>
  );
};
